import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MarkdownRenderer from './MarkdownRenderer';
import DocReferencePopup from './DocReferencePopup';
import { useIsMobile } from '../hooks/useIsMobile';
import { useAppStore } from '../stores/appStore';
import { useFileEditorStore, type ModelOption } from '../stores/fileEditorStore';
import { toFriendlyErrorMessage } from '../utils/friendlyErrors';

function formatProvider(provider: string): string {
  if (!provider) return 'Models';
  return provider
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function groupModelsByProvider(models: ModelOption[]) {
  return models.reduce<Record<string, ModelOption[]>>((groups, model) => {
    const key = model.provider || 'other';
    if (!groups[key]) {
      groups[key] = [];
    }
    groups[key].push(model);
    return groups;
  }, {});
}

function formatFileSize(size?: number): string | null {
  if (typeof size !== 'number' || Number.isNaN(size)) return null;
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function CopyablePath({ path }: { path: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(path).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="text-muted-foreground hover:text-foreground transition-colors truncate"
      style={{ fontSize: '10px', fontFamily: 'var(--app-code-font)' }}
      title="Click to copy path"
    >
      {copied ? 'Copied!' : path}
    </button>
  );
}

export default function FileEditorPanel() {
  const {
    open,
    view,
    agents,
    archivedAgents,
    projects,
    loadingProjects,
    selectedSourceType,
    selectedAgentId,
    selectedAgentArchived,
    selectedProjectName,
    currentPath,
    files,
    loadingFiles,
    selectedFile,
    fileContent,
    editContent,
    loadingContent,
    saving,
    archivingAgentId,
    versions,
    loadingVersions,
    viewingVersion,
    versionContent,
    templates,
    loadingTemplates,
    templateError,
    models,
    loadingModels,
    modelError,
    selectedTemplate,
    newAgentName,
    selectedModelId,
    creatingAgent,
    creationError,
    successMessage,
    closePanel,
    selectAgent,
    selectProject,
    selectFile,
    navigateToFolder,
    navigateUp,
    navigateToBreadcrumb,
    startEditing,
    setEditContent,
    saveFile,
    loadVersions,
    viewVersion,
    loadTemplates,
    loadModels,
    loadAgents,
    loadProjects,
    openCreateAgentFlow,
    selectTemplate,
    setNewAgentName,
    setSelectedModel,
    createAgent,
    archiveAgent,
    renameFile,
    goBack,
  } = useFileEditorStore();

  const isMobile = useIsMobile();
  const lastReconnectAt = useAppStore((state) => state.lastReconnectAt);

  const [confirmArchiveAgent, setConfirmArchiveAgent] = useState<{ id: string; label: string } | null>(null);
  const [docRefPath, setDocRefPath] = useState<string | null>(null);

  // Inline rename state
  const [renamingFile, setRenamingFile] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);

  const startRename = useCallback((fileName: string) => {
    // Guard: if file is open in editor with unsaved changes, block rename
    if (selectedFile?.name === fileName && editContent !== fileContent && view === 'editor') {
      setRenameError('Save changes before renaming');
      return;
    }
    setRenamingFile(fileName);
    setRenameValue(fileName);
    setRenameError(null);
    // Select basename (not extension) for usability
    requestAnimationFrame(() => {
      const input = renameInputRef.current;
      if (input) {
        input.focus();
        const dotIndex = fileName.lastIndexOf('.');
        input.setSelectionRange(0, dotIndex > 0 ? dotIndex : fileName.length);
      }
    });
  }, [selectedFile, editContent, fileContent, view]);

  const submitRename = useCallback(async () => {
    if (!renamingFile || !renameValue.trim() || renameValue === renamingFile) {
      setRenamingFile(null);
      setRenameError(null);
      return;
    }
    try {
      await renameFile(renamingFile, renameValue.trim());
      setRenamingFile(null);
      setRenameError(null);
    } catch (err: any) {
      setRenameError(err?.message ?? 'Rename failed');
    }
  }, [renamingFile, renameValue, renameFile]);

  const cancelRename = useCallback(() => {
    setRenamingFile(null);
    setRenameError(null);
  }, []);

  const handleDocReference = useCallback((path: string) => {
    setDocRefPath(path);
  }, []);

  const handleCloseDocRef = useCallback(() => {
    setDocRefPath(null);
  }, []);

  // Compute the directory context for resolving relative paths in doc references.
  // For project files: currentPath is the folder being browsed (already a directory).
  // For game files: derive directory from the full gameFilePath.
  const docRefCurrentPath = useMemo(() => {
    if (selectedSourceType === 'game' && useFileEditorStore.getState().gameFilePath) {
      const gfp = useFileEditorStore.getState().gameFilePath!;
      const lastSlash = gfp.lastIndexOf('/');
      return lastSlash >= 0 ? gfp.slice(0, lastSlash) : '';
    }
    return currentPath;
  }, [selectedSourceType, currentPath]);

  const docRefContext = useMemo(() => ({
    sourceType: selectedSourceType,
    agentId: selectedAgentId,
    archived: selectedAgentArchived,
    projectName: selectedProjectName,
    currentPath: docRefCurrentPath,
  }), [selectedSourceType, selectedAgentId, selectedAgentArchived, selectedProjectName, docRefCurrentPath]);

  const modelGroups = useMemo(() => groupModelsByProvider(models), [models]);
  const isCreatingFlow = view === 'template-picker' || view === 'create-agent';
  const selectedActiveAgent = agents.find((agent) => agent.id === selectedAgentId) ?? null;
  const selectedArchivedAgent = archivedAgents.find((agent) => agent.id === selectedAgentId) ?? null;
  const hasAnyAgents = agents.length > 0 || archivedAgents.length > 0;
  const hasAnyProjects = projects.length > 0;
  const selectedAgentName = selectedAgentArchived
    ? (selectedArchivedAgent?.name ?? selectedAgentId ?? 'this agent')
    : (selectedActiveAgent?.identity?.name ?? selectedActiveAgent?.name ?? selectedAgentId ?? 'this agent');
  const isProjectSelected = selectedSourceType === 'project';
  const isGameFile = selectedSourceType === 'game';

  // Sort files: directories first, then files, most recently modified first within each group
  const sortedFiles = useMemo(() => {
    const sorted = [...files];
    sorted.sort((a, b) => {
      const aDir = a.type === 'directory' ? 0 : 1;
      const bDir = b.type === 'directory' ? 0 : 1;
      if (aDir !== bDir) return aDir - bDir;
      // Sort by mtime descending (most recent first) if available
      if (a.mtime != null && b.mtime != null) return b.mtime - a.mtime;
      if (a.mtime != null) return -1;
      if (b.mtime != null) return 1;
      return a.name.localeCompare(b.name);
    });
    return sorted;
  }, [files]);
  const selectedProject = projects.find((project) => project.name === selectedProjectName) ?? null;
  const selectedProjectLabel = selectedProject?.name ?? selectedProjectName ?? 'project';
  const selectedArchivableAgent = !isCreatingFlow && selectedSourceType === 'agent' && !selectedAgentArchived && selectedActiveAgent?.id !== 'main'
    ? selectedActiveAgent
    : null;

  // Close on Escape key
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') goBack();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, goBack]);

  useEffect(() => {
    if (!open || !lastReconnectAt) {
      return;
    }

    void loadAgents();
    void loadProjects();
  }, [open, lastReconnectAt, loadAgents, loadProjects]);

  if (!open) return null;

  const getAgentLabel = (agent: { id: string; identity?: { name: string; emoji?: string }; name?: string; emoji?: string }) => {
    const name = agent.identity?.name ?? agent.name ?? agent.id;
    const emoji = agent.identity?.emoji ?? agent.emoji;
    return emoji ? `${emoji} ${name}` : name;
  };

  const handleArchiveAgent = (agent: { id: string; identity?: { name: string; emoji?: string }; name?: string }) => {
    const label = agent.identity?.name ?? agent.name ?? agent.id;
    setConfirmArchiveAgent({ id: agent.id, label });
  };

  const confirmArchive = () => {
    if (!confirmArchiveAgent) return;
    const agentId = confirmArchiveAgent.id;
    setConfirmArchiveAgent(null);
    void archiveAgent(agentId).catch((error) => {
      console.error('[FileEditorPanel] Failed to archive agent:', error);
      window.alert(toFriendlyErrorMessage(error, 'agent-archive'));
    });
  };

  const headerTitle = () => {
    if (viewingVersion) return 'Version';
    if (view === 'editor') return `Editing ${selectedFile?.name ?? ''}`;
    if (view === 'history') return `History — ${selectedFile?.name ?? ''}`;
    if (view === 'viewer') return selectedFile?.name ?? '';
    if (view === 'create-agent') return 'Create Agent';
    if (view === 'template-picker') return 'New Agent';
    if (isGameFile) return 'Game Files';
    if (isProjectSelected && selectedProjectName) return selectedProjectName;
    if (isProjectSelected) return 'Projects';
    return 'Workshop';
  };

  const formatTimestamp = (ts: string) => {
    if (!ts) return 'Unknown';
    try {
      const d = new Date(ts);
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return ts;
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-background text-foreground safe-area-top">
      {/* Header bar */}
      <header className="flex items-center justify-between border-b border-border bg-card px-3 py-2 shrink-0">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Back
        </button>

        <span className="text-sm font-semibold text-foreground truncate px-2 max-w-[50%]">
          {headerTitle()}
        </span>

        <button
          type="button"
          onClick={view === 'viewer' || view === 'editor' || view === 'history' ? goBack : closePanel}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Close
        </button>
      </header>

      {/* Agent chips + create action - hidden when viewing/editing a file or game files */}
      {!isGameFile && view !== 'viewer' && view !== 'editor' && view !== 'history' && (
        <div className="border-b border-border bg-card shrink-0">
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="min-w-0 flex-1 overflow-x-auto">
              <div className="flex w-max gap-2">
                {agents.map((agent) => {
                  const isSelected = selectedSourceType === 'agent' && selectedAgentId === agent.id && !selectedAgentArchived && !isCreatingFlow;

                  return (
                    <button
                      key={agent.id}
                      type="button"
                      onClick={() => selectAgent(agent.id)}
                      className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-secondary text-secondary-foreground hover:bg-muted'
                      }`}
                    >
                      {getAgentLabel(agent)}
                    </button>
                  );
                })}

                <button
                  type="button"
                  onClick={() => void openCreateAgentFlow()}
                  className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    isCreatingFlow
                      ? 'border-ring bg-primary/10 text-foreground'
                      : 'border-dashed border-input bg-background text-muted-foreground hover:border-ring hover:text-foreground'
                  }`}
                >
                  + New Agent
                </button>
              </div>
            </div>

            {selectedArchivableAgent && (
              <button
                type="button"
                onClick={() => handleArchiveAgent(selectedArchivableAgent)}
                disabled={archivingAgentId === selectedArchivableAgent.id}
                aria-label={`Archive ${selectedAgentName}`}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-input/70 bg-background text-muted-foreground transition-colors hover:border-destructive/40 hover:text-destructive disabled:cursor-not-allowed disabled:opacity-60"
                title="Archive agent"
              >
                {archivingAgentId === selectedArchivableAgent.id ? (
                  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current/30 border-t-current" />
                ) : (
                  <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 8v13H3V8" />
                    <path d="M1 3h22v5H1z" />
                    <path d="M10 12h4" />
                  </svg>
                )}
              </button>
            )}
          </div>

          {archivedAgents.length > 0 && (
            <div className="border-t border-border/70 px-3 py-2">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                📦 Archived
              </div>
              <div className="flex gap-2 overflow-x-auto">
                {archivedAgents.map((agent) => {
                  const isSelected = selectedSourceType === 'agent' && selectedAgentId === agent.id && selectedAgentArchived && !isCreatingFlow;
                  return (
                    <button
                      key={agent.id}
                      type="button"
                      onClick={() => selectAgent(agent.id, true)}
                      className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                        isSelected
                          ? 'border-amber-500/40 bg-amber-500/10 text-foreground'
                          : 'border-border bg-background text-muted-foreground hover:border-input hover:text-foreground'
                      }`}
                      title={agent.vibe ?? `Archived workspace for ${agent.name}`}
                    >
                      {getAgentLabel(agent)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="border-t border-border/70 px-3 py-2">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Projects
            </div>
            {loadingProjects && projects.length === 0 ? (
              <div className="text-xs text-muted-foreground">Loading projects...</div>
            ) : projects.length === 0 ? (
              <div className="text-xs text-muted-foreground">No projects found in ~/projects.</div>
            ) : (
              <div className="flex gap-2 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                {projects.map((project) => {
                  const isSelected = selectedSourceType === 'project' && selectedProjectName === project.name && !isCreatingFlow;
                  return (
                    <button
                      key={project.name}
                      type="button"
                      onClick={() => selectProject(project.name)}
                      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                        isSelected
                          ? 'border-sky-500/40 bg-sky-500/10 text-foreground'
                          : 'border-border bg-background text-muted-foreground hover:border-input hover:text-foreground'
                      }`}
                      title={`Open ${project.name}`}
                    >
                      <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                      </svg>
                      <span>{project.name}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* ── File list view ── */}
        {view === 'file-list' && (
          <div className="p-3 space-y-3">
            {successMessage && (
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-foreground">
                {successMessage}
              </div>
            )}

            {selectedSourceType === 'agent' && selectedAgentArchived && selectedAgentId && (
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-foreground">
                Archived workspace for {selectedAgentName} — read only.
              </div>
            )}

            {/* Breadcrumb navigation for projects or agent subfolders */}
            {((selectedSourceType === 'project' && selectedProjectName) || (selectedSourceType === 'agent' && selectedAgentId && currentPath)) && (
              <div className={`rounded-xl border px-3 py-2 text-sm text-foreground ${selectedSourceType === 'project' ? 'border-sky-500/30 bg-sky-500/10' : 'border-primary/30 bg-primary/10'}`}>
                <div className="flex items-center flex-wrap gap-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => navigateToBreadcrumb(-1)}
                    className="font-semibold text-foreground hover:text-sky-500 transition-colors"
                  >
                    {selectedSourceType === 'project' ? selectedProjectLabel : selectedAgentName}
                  </button>
                  {currentPath.split('/').filter(Boolean).map((segment, idx) => (
                    <span key={idx} className="flex items-center gap-0.5">
                      <span className="text-muted-foreground">/</span>
                      <button
                        type="button"
                        onClick={() => navigateToBreadcrumb(idx)}
                        className="text-foreground hover:text-sky-500 transition-colors"
                      >
                        {segment}
                      </button>
                    </span>
                  ))}
                </div>
                {selectedSourceType === 'project' && selectedProjectName && (
                  <CopyablePath path={`~/projects/${selectedProjectName}${currentPath ? `/${currentPath}` : ''}`} />
                )}
              </div>
            )}

            {!hasAnyAgents && !hasAnyProjects ? (
              <div className="rounded-xl border border-dashed border-input bg-card px-4 py-8 text-center">
                <div className="text-3xl">🛠️</div>
                <h3 className="mt-3 text-base font-semibold text-foreground">Welcome to Workshop</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Create your first agent to start building.
                </p>
                <button
                  type="button"
                  onClick={() => void openCreateAgentFlow()}
                  className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
                >
                  New Agent
                </button>
              </div>
            ) : loadingFiles ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                Loading files...
              </div>
            ) : selectedSourceType === 'project' && !selectedProjectName ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                Pick a project above to browse its markdown files.
              </div>
            ) : selectedSourceType === 'agent' && !selectedAgentId ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                Pick an agent above to browse its files.
              </div>
            ) : files.length === 0 && !loadingFiles && !currentPath ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                No files found
              </div>
            ) : (
              <div className="space-y-2">
                {/* "↑ Up" entry when inside a subfolder */}
                {currentPath && (
                  <button
                    type="button"
                    onClick={navigateUp}
                    className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-secondary transition-colors text-muted-foreground text-xs"
                  >
                    <span>↑</span>
                    <span>Up</span>
                  </button>
                )}
                {files.length === 0 && currentPath && (
                  <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                    Empty folder
                  </div>
                )}

                {/* Folders — compact grid */}
                {sortedFiles.some((f) => f.type === 'directory') && (
                  <div className="db-gf-folders-grid">
                    {sortedFiles.filter((f) => f.type === 'directory').map((folder) => (
                      <button
                        key={folder.name}
                        type="button"
                        className="db-gf-folder-item"
                        onClick={() => navigateToFolder(folder.name)}
                      >
                        <span className="db-gf-folder-icon">📁</span>
                        <span className="db-gf-folder-name">{folder.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Files — vertical list */}
                {sortedFiles.some((f) => f.type !== 'directory') && (
                  <ul className="space-y-1">
                    {sortedFiles.filter((f) => f.type !== 'directory').map((file) => {
                      const isTxt = file.name.endsWith('.txt');
                      const sizeLabel = selectedSourceType === 'project' && formatFileSize(file.size);
                      const isRenaming = renamingFile === file.name;
                      const canRename = selectedSourceType === 'agent' || selectedSourceType === 'project';
                      const fileIcon = isTxt ? (
                        <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-amber-500" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                      );

                      if (isRenaming) {
                        return (
                          <li key={file.name}>
                            <div className="flex items-center gap-3 rounded-lg px-3 py-2.5">
                              {fileIcon}
                              <div className="min-w-0 flex-1">
                                <input
                                  ref={renameInputRef}
                                  type="text"
                                  value={renameValue}
                                  onChange={(e) => { setRenameValue(e.target.value); setRenameError(null); }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') { e.preventDefault(); void submitRename(); }
                                    if (e.key === 'Escape') { cancelRename(); }
                                  }}
                                  onBlur={() => void submitRename()}
                                  className="w-full rounded px-2 py-1 text-sm outline-none"
                                  style={{ border: '1px solid var(--theme-border, #333)', background: 'var(--theme-card, #1a1a1a)', color: 'var(--theme-foreground, #eee)' }}
                                />
                                {renameError && (
                                  <div className="mt-1 text-xs" style={{ color: '#ef4444' }}>{renameError}</div>
                                )}
                              </div>
                            </div>
                          </li>
                        );
                      }

                      return (
                        <li key={file.name} className="group">
                          <div
                            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-secondary transition-colors cursor-pointer"
                            onClick={() => void selectFile(file)}
                            onKeyDown={(e) => { if (e.key === 'F2' && canRename) startRename(file.name); }}
                            onDoubleClick={(e) => { if (canRename) { e.stopPropagation(); startRename(file.name); } }}
                            tabIndex={0}
                            role="button"
                          >
                            {fileIcon}
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm text-foreground">{file.name}</div>
                              {sizeLabel && (
                                <div className="mt-0.5 text-xs text-muted-foreground">{sizeLabel}</div>
                              )}
                            </div>
                            {canRename && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); startRename(file.name); }}
                                className="opacity-0 group-hover:opacity-100 shrink-0 p-1 rounded hover:bg-secondary transition-opacity"
                                title="Rename"
                              >
                                <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                </svg>
                              </button>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Template picker ── */}
        {view === 'template-picker' && (
          <div className="p-4 md:p-5 space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Choose a template</h3>
                <p className="text-sm text-muted-foreground">
                  Start with a personality, then give your new agent a name and brain.
                </p>
              </div>
              <button
                type="button"
                onClick={goBack}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Cancel
              </button>
            </div>

            {templateError && templates.length > 0 && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive-foreground flex items-center justify-between gap-3">
                <span>{templateError}</span>
                <button
                  type="button"
                  onClick={() => void loadTemplates()}
                  className="rounded-md border border-input px-2.5 py-1 text-xs text-foreground hover:bg-secondary transition-colors"
                >
                  Retry
                </button>
              </div>
            )}

            {loadingTemplates && templates.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                Loading templates...
              </div>
            ) : templateError && templates.length === 0 ? (
              <div className="rounded-xl border border-destructive/30 bg-card px-4 py-8 text-center">
                <div className="text-2xl">⚠️</div>
                <p className="mt-3 text-sm text-destructive-foreground">{templateError}</p>
                <button
                  type="button"
                  onClick={() => void loadTemplates()}
                  className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {templates.map((template) => {
                  const isBlank = template.id === 'blank';
                  const isSelected = selectedTemplate?.id === template.id;

                  return (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => selectTemplate(template.id)}
                      className={`group flex min-h-[180px] flex-col rounded-2xl border p-4 text-left transition-all ${
                        isSelected
                          ? 'border-ring ring-2 ring-ring bg-card'
                          : isBlank
                            ? 'border-dashed border-input bg-card/70 hover:border-ring hover:bg-card'
                            : 'border-border bg-card hover:border-input hover:bg-card/90'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-3xl shadow-sm">
                          <span aria-hidden="true">{template.emoji}</span>
                        </div>
                        {isBlank && (
                          <span className="rounded-full border border-dashed border-input px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                            Start from scratch
                          </span>
                        )}
                      </div>

                      <div className="mt-4 space-y-2">
                        <h4 className="text-base font-semibold text-foreground group-hover:text-foreground">
                          {template.name}
                        </h4>
                        <p className="text-sm leading-6 text-muted-foreground">
                          {template.description}
                        </p>
                      </div>

                      <div className="mt-auto pt-4 text-xs font-medium text-muted-foreground group-hover:text-foreground">
                        Choose template →
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Create agent view ── */}
        {view === 'create-agent' && selectedTemplate && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void createAgent();
            }}
            className="mx-auto flex max-w-2xl flex-col gap-4 p-4 md:p-5"
          >
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                <span aria-hidden="true">{selectedTemplate.emoji}</span>
                {selectedTemplate.name}
              </span>
              <button
                type="button"
                onClick={goBack}
                disabled={creatingAgent}
                className="text-sm text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
              >
                ← Back to templates
              </button>
            </div>

            <div className="rounded-2xl border border-border bg-card p-4 md:p-5 space-y-5 shadow-sm">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Configure your new agent</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Give it a name, pick a model, and we&apos;ll handle the rest.
                </p>
              </div>

              <label className="block space-y-2">
                <span className="text-xs uppercase tracking-wide text-secondary-foreground">Agent name</span>
                <input
                  type="text"
                  value={newAgentName}
                  onChange={(event) => setNewAgentName(event.target.value)}
                  placeholder="e.g., Atlas, Mei, Scout..."
                  disabled={creatingAgent}
                  autoFocus
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring disabled:opacity-60"
                />
              </label>

              <label className="block space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-xs uppercase tracking-wide text-secondary-foreground">Model</span>
                  {modelError && (
                    <button
                      type="button"
                      onClick={() => void loadModels()}
                      className="text-xs text-foreground underline underline-offset-4 hover:text-primary"
                    >
                      Retry
                    </button>
                  )}
                </div>
                <select
                  value={selectedModelId}
                  onChange={(event) => setSelectedModel(event.target.value)}
                  disabled={creatingAgent || loadingModels || models.length === 0}
                  className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring disabled:opacity-60"
                >
                  {loadingModels && models.length === 0 ? (
                    <option value="">Loading models...</option>
                  ) : models.length === 0 ? (
                    <option value="">No models available</option>
                  ) : (
                    Object.entries(modelGroups).map(([provider, providerModels]) => (
                      <optgroup key={provider} label={formatProvider(provider)}>
                        {providerModels.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.name}
                          </option>
                        ))}
                      </optgroup>
                    ))
                  )}
                </select>
                {loadingModels && models.length > 0 && (
                  <p className="text-xs text-muted-foreground">Refreshing available models…</p>
                )}
                {modelError && <p className="text-xs text-destructive-foreground">{modelError}</p>}
                {!loadingModels && !modelError && models.length === 0 && (
                  <p className="text-xs text-muted-foreground">No models were returned by the API.</p>
                )}
              </label>

              {creationError && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive-foreground">
                  {creationError}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 pt-2">
                <button
                  type="button"
                  onClick={goBack}
                  disabled={creatingAgent}
                  className="rounded-lg border border-input px-3 py-2 text-sm text-secondary-foreground transition-colors hover:bg-muted disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={creatingAgent || !newAgentName.trim() || !selectedModelId}
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {creatingAgent && (
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                  )}
                  {creatingAgent ? 'Creating Agent...' : 'Create Agent'}
                </button>
              </div>
            </div>
          </form>
        )}

        {/* ── Viewer view ── */}
        {view === 'viewer' && (
          <div className="flex flex-col h-full">
            {/* Action bar */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-card shrink-0">
              {selectedSourceType === 'agent' && selectedAgentArchived ? (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs font-medium text-foreground">
                  Archived — read only
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={startEditing}
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary-hover transition-colors"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={loadVersions}
                    className="rounded-lg border border-input px-3 py-1.5 text-xs font-medium text-card-foreground hover:bg-secondary transition-colors"
                  >
                    History
                  </button>
                </>
              )}
              {selectedSourceType === 'project' && selectedProjectName && selectedFile && (
                <CopyablePath path={`~/projects/${selectedProjectName}${currentPath ? `/${currentPath}` : ''}/${selectedFile.name}`} />
              )}
              {selectedSourceType === 'agent' && selectedAgentId && !selectedAgentArchived && selectedFile && (
                <CopyablePath path={`~/.clawletgo/agents/${selectedAgentId}${currentPath ? `/${currentPath}` : ''}/${selectedFile.name}`} />
              )}
            </div>

            {/* File content */}
            <div className={`flex-1 overflow-y-auto ${isMobile ? 'px-4 py-4' : 'px-6 py-5 max-w-3xl mx-auto w-full'}`}>
              {loadingContent ? (
                <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                  Loading...
                </div>
              ) : (selectedFile?.name?.endsWith('.md') || selectedFile?.name?.endsWith('.txt')) ? (
                <MarkdownRenderer content={fileContent} onDocReference={handleDocReference} />
              ) : (
                <MarkdownRenderer content={`\`\`\`${(selectedFile?.name?.split('.').pop() ?? '').toLowerCase()}\n${fileContent}\n\`\`\``} />
              )}
            </div>
          </div>
        )}

        {/* ── Editor view ── */}
        {view === 'editor' && (
          <div className="flex flex-col h-full">
            {/* Save bar */}
            <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-card shrink-0">
              <button
                type="button"
                onClick={() => void saveFile()}
                disabled={saving}
                className="rounded-lg bg-primary px-4 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary-hover transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={goBack}
                className="rounded-lg border border-input px-3 py-1.5 text-xs font-medium text-card-foreground hover:bg-secondary transition-colors"
              >
                Cancel
              </button>
            </div>

            {/* Textarea */}
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="flex-1 min-h-0 w-full resize-none bg-background p-4 text-sm text-foreground outline-none"
              style={{
                fontFamily: 'var(--app-code-font)',
                lineHeight: 1.6,
                fieldSizing: 'fixed',
              }}
              spellCheck={false}
              autoFocus
            />
          </div>
        )}

        {/* ── History view ── */}
        {view === 'history' && !viewingVersion && (
          <div className="p-3">
            {loadingVersions ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                Loading versions...
              </div>
            ) : versions.length === 0 ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                No previous versions
              </div>
            ) : (
              <ul className="space-y-1">
                {versions.map((v) => (
                  <li key={v.name}>
                    <button
                      type="button"
                      onClick={() => viewVersion(v)}
                      className="w-full flex items-center gap-3 rounded-lg px-3 py-3 text-left hover:bg-secondary transition-colors"
                    >
                      <svg viewBox="0 0 24 24" className="h-5 w-5 shrink-0 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span className="text-sm text-foreground">{formatTimestamp(v.timestamp)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ── Version viewer ── */}
        {view === 'history' && viewingVersion && (
          <div className={`flex-1 overflow-y-auto ${isMobile ? 'px-4 py-4' : 'px-6 py-5 max-w-3xl mx-auto w-full'}`}>
            {loadingContent ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground text-sm">
                Loading...
              </div>
            ) : (
              <MarkdownRenderer content={versionContent} onDocReference={handleDocReference} />
            )}
          </div>
        )}
      </div>

      {/* Doc reference popup */}
      {docRefPath && (
        <DocReferencePopup
          filePath={docRefPath}
          context={docRefContext}
          onClose={handleCloseDocRef}
        />
      )}

      {/* Archive confirmation dialog */}
      {confirmArchiveAgent && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60">
          <div className="mx-4 w-full max-w-sm rounded-2xl border border-border bg-card p-5 shadow-lg">
            <h3 className="text-base font-semibold text-foreground">
              Remove {confirmArchiveAgent.label}?
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              They won&apos;t appear in chat anymore. Workspace files are archived and can still be viewed.
            </p>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmArchiveAgent(null)}
                className="rounded-lg border border-input px-3 py-2 text-sm font-medium text-secondary-foreground transition-colors hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmArchive}
                className="rounded-lg bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/80"
              >
                Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
